import type {
  AuthResponse,
  BilingualErrorBody,
  ClientListItem,
  EmployeeListItem,
  NenmatsuData,
  NenmatsuMine,
  PunchRecord,
} from './types';

const TOKEN_KEY = 'kintai.employee.token';
const SELF_KEY = 'kintai.employee.self';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(SELF_KEY);
}

export function setSelf(employee: AuthResponse['employee']): void {
  localStorage.setItem(SELF_KEY, JSON.stringify(employee));
}

export function getSelf(): AuthResponse['employee'] | null {
  const raw = localStorage.getItem(SELF_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthResponse['employee'];
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: BilingualErrorBody | null,
  ) {
    super(`API ${status}`);
  }
}

/**
 * 401 ハンドラ。従業員側でも JWT 期限切れ時にトークンをクリアして
 * 名前選択画面 (`/?expired=1`) へリダイレクトする。
 *
 * `/api/auth/pin` 自体の 401（PIN 違い）はリダイレクトしてはいけないので除外する。
 */
function handleUnauthorized(path: string): void {
  if (path.includes('/auth/pin')) return;
  clearToken();
  if (typeof window !== 'undefined' && window.location.pathname !== '/') {
    window.location.href = '/?expired=1';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers = new Headers(init.headers);
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const res = await fetch(path, { ...init, headers, credentials: 'include' });
  if (!res.ok) {
    let body: BilingualErrorBody | null = null;
    try {
      body = (await res.json()) as BilingualErrorBody;
    } catch {
      // ignore
    }
    if (res.status === 401) {
      handleUnauthorized(path);
    }
    throw new ApiError(res.status, body);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  listEmployees: () =>
    request<{ employees: EmployeeListItem[] }>('/api/employees').then((r) => r.employees),

  verifyPin: (employee_id: string, pin: string) =>
    request<AuthResponse>('/api/auth/pin', {
      method: 'POST',
      body: JSON.stringify({ employee_id, pin }),
    }),

  listClients: () =>
    request<{ clients: ClientListItem[] }>('/api/clients').then((r) => r.clients),

  punch: (client_id: string, punch_type: 'in' | 'out') =>
    request<{ punch: PunchRecord }>('/api/punch', {
      method: 'POST',
      body: JSON.stringify({ client_id, punch_type }),
    }).then((r) => r.punch),

  todayPunches: () =>
    request<{ punches: PunchRecord[] }>('/api/punches/today').then((r) => r.punches),

  changeMyPin: (current_pin: string, new_pin: string) =>
    request<{ ok: true }>('/api/auth/pin-change', {
      method: 'POST',
      body: JSON.stringify({ current_pin, new_pin }),
    }),

  getNenmatsu: (year?: number) =>
    request<NenmatsuMine>(`/api/nenmatsu/me${year ? `?year=${year}` : ''}`),

  saveNenmatsu: (data: NenmatsuData, submit: boolean, year?: number) =>
    request<{ ok: true; year: number; submitted: boolean }>('/api/nenmatsu/me', {
      method: 'PUT',
      body: JSON.stringify({ data, submit, ...(year ? { year } : {}) }),
    }),
};
